/**
 * What a change of a request's terms changes (D25), field by field, in the words the brand and
 * the admin team read on the request page: "Budget: 1,000 USDC → 1,500 USDC". The brand sees it
 * while the change waits for review, and the admin team reviews it.
 */

import { type CampaignRequestInput, primaryMetric } from "@morcast/campaigns";

import { formatAmount, formatCount, formatWindow } from "../format.js";
import type { CampaignRequest } from "./requests.js";

/** One field a change changes, with its value now and the value asked for. */
export interface TermDifference {
  label: string;
  from: string;
  to: string;
}

/** A token as the page names it: its symbol and decimals when known. */
type Token = CampaignRequest["token"];

/** "X", "YouTube, dedicated video" or "YouTube, integration". */
function platformName(platform: string, format: string | null): string {
  if (platform === "X") {
    return "X";
  }
  return format === "INTEGRATION" ? "YouTube, integration" : "YouTube, dedicated video";
}

/** A list as one line, or "None" when empty. */
function list(items: readonly string[] | undefined): string {
  return items && items.length > 0 ? items.join(", ") : "None";
}

/** A text, or "None" when empty. */
function text(value: string | undefined): string {
  return value?.trim() ? value : "None";
}

/**
 * The fields `terms` changes in `request`, in the order of the request form. Empty when nothing
 * changes.
 *
 * @param tokens The tokens the page knows, to name a token the change switches to. Without it,
 *               such a token is named by its address.
 */
export function termDifferences(
  request: CampaignRequest,
  terms: CampaignRequestInput,
  tokens: readonly Token[] = [],
): TermDifference[] {
  const newToken: Token =
    terms.token.toLowerCase() === request.token.address.toLowerCase()
      ? request.token
      : (tokens.find((entry) => entry.address.toLowerCase() === terms.token.toLowerCase()) ?? {
          address: terms.token,
          symbol: null,
          decimals: null,
        });
  const unitNow = primaryMetric(request.platform, request.format).unit;
  const unitAsked = primaryMetric(terms.platform, terms.format ?? null).unit;
  const before = request.brief;
  const after = terms.brief;

  const fields: TermDifference[] = [
    { label: "Brand", from: request.brandName, to: terms.brandName },
    { label: "Contact", from: request.contactEmail, to: terms.contactEmail },
    {
      label: "Platform",
      from: platformName(request.platform, request.format),
      to: platformName(terms.platform, terms.format ?? null),
    },
    {
      label: "Token",
      from: request.token.symbol ?? request.token.address,
      to: newToken.symbol ?? newToken.address,
    },
    {
      label: "Budget",
      from: formatAmount(request.budget, request.token),
      to: formatAmount(String(terms.budget), newToken),
    },
    {
      label: "Target",
      from: `${formatCount(request.target)} ${unitNow}`,
      to: `${formatCount(String(terms.target))} ${unitAsked}`,
    },
    {
      label: "Window",
      from: formatWindow(request.platform, request.startAt, request.endAt),
      to: formatWindow(terms.platform, String(terms.startAt), String(terms.endAt)),
    },
    {
      label: "Submissions per account",
      from: String(request.submissionLimit),
      to: String(terms.submissionLimit),
    },
    { label: "Title", from: before.title, to: after.title },
    { label: "Description", from: before.description, to: after.description },
    { label: "Disclosure", from: before.disclosure, to: after.disclosure ?? before.disclosure },
    { label: "Hashtags", from: list(before.requiredHashtags), to: list(after.requiredHashtags) },
    { label: "Mentions", from: list(before.requiredMentions), to: list(after.requiredMentions) },
    { label: "Links", from: list(before.requiredLinks), to: list(after.requiredLinks) },
    {
      label: "Required phrases",
      from: list(before.requiredText),
      to: list(after.requiredText),
    },
    {
      label: "Content requirements",
      from: list(before.semanticRequirements),
      to: list(after.semanticRequirements),
    },
    {
      label: "Visual requirements",
      from: list(before.visualRequirements),
      to: list(after.visualRequirements),
    },
    {
      label: "Prohibited content",
      from: list(before.prohibitedContent),
      to: list(after.prohibitedContent),
    },
    { label: "Allowed edits", from: text(before.allowedEdits), to: text(after.allowedEdits) },
    {
      label: "Brand kit",
      from: list(before.assets?.map((asset) => asset.label)),
      // An image is marked new when the kit did not have it, even under the same name.
      to: list(
        after.assets?.map((asset) =>
          before.assets?.some((kept) => kept.sha256 === asset.sha256)
            ? asset.label
            : `${asset.label} (new image)`,
        ),
      ),
    },
  ];
  return fields.filter((field) => field.from !== field.to);
}
